import { memo, useEffect, useMemo, useRef } from "react";
import {
  buildBeatGrid,
  getActiveChordAtTime,
  getChordChangeAtBeat,
  getChordLabel,
  normalizeChordChanges,
} from "@/music/chords/chordSyncUtils";

const formatTime = (seconds = 0) => {
  const safe = Math.max(0, Number(seconds) || 0);
  return `${Math.floor(safe / 60)}:${String(Math.floor(safe % 60)).padStart(2, "0")}`;
};

// Responsive font class for chord labels — prevents overflow on long names.
const chordLabelSize = (len) => {
  if (len <= 2) return "text-2xl sm:text-3xl";
  if (len <= 3) return "text-xl sm:text-2xl";
  if (len <= 4) return "text-lg sm:text-xl";
  if (len <= 6) return "text-sm sm:text-base";
  return "text-xs sm:text-sm";
};

const ChordGridComposer = memo(({
  events = [],
  bpm = 120,
  duration = 0,
  currentTime = 0,
  beatsPerBar = 4,
  barsPerRow = 4,
  subdivision = 1,
  mode = "practice",
  selectedCell = null,
  selectedChangeId = null,
  onSeek,
  onAddChordChange,
  onUpdateChordChange,
  onRemoveChordChange,
  onSelectCell,
  autoFollow = true,
  zoom = 1,
}) => {
  const rowRefs = useRef([]);
  const changes = useMemo(() => normalizeChordChanges(events, duration), [duration, events]);
  const gridDuration = Math.max(Number(duration) || 0, changes[changes.length - 1]?.end || 0, 4);
  const beatCells = useMemo(
    () => buildBeatGrid({ duration: gridDuration, bpm, beatsPerBar, subdivision }),
    [beatsPerBar, bpm, gridDuration, subdivision]
  );
  const cellsPerRow = Math.max(1, beatsPerBar * barsPerRow * subdivision);
  const rows = useMemo(() => {
    const grouped = [];
    for (let index = 0; index < beatCells.length; index += cellsPerRow) {
      grouped.push(beatCells.slice(index, index + cellsPerRow));
    }
    return grouped;
  }, [beatCells, cellsPerRow]);
  const matchedCellIndex = beatCells.findIndex(
    (cell) => currentTime >= cell.start && currentTime < cell.end
  );
  const activeCellIndex = matchedCellIndex >= 0
    ? matchedCellIndex
    : currentTime >= beatCells[beatCells.length - 1]?.end
      ? beatCells.length - 1
      : 0;
  const activeRowIndex = Math.floor(activeCellIndex / cellsPerRow);
  const activeChord = getActiveChordAtTime(changes, currentTime);
  const isEdit = mode === "edit";
  const cellWidth = Math.round(Math.max(72, (isEdit ? 88 : 80) * Number(zoom || 1)));
  // Total grid width so all rows share the same horizontal scroll axis.
  const gridWidth = cellsPerRow * cellWidth;

  useEffect(() => {
    if (!autoFollow || activeRowIndex < 0) return;
    rowRefs.current[activeRowIndex]?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [activeRowIndex, autoFollow]);

  return (
    // Outer container: styled wrapper with NO overflow — keeps overflow-y: visible so rows are never clipped.
    // Horizontal scroll lives on the inner div to avoid the CSS spec coercion (overflow-x: auto → overflow-y: auto).
    <div className="rounded-2xl border border-white/10 bg-[#090d16]/80 p-3 sm:p-4">
      <div className="steel-scroll-soft overflow-x-auto" style={{ minWidth: 0 }}>
        <div style={{ minWidth: `${gridWidth}px` }}>
        {rows.map((row, rowIndex) => {
          const activeRow = rowIndex === activeRowIndex;
          return (
            <div
              key={rowIndex}
              ref={(node) => { rowRefs.current[rowIndex] = node; }}
              className={`flex border-b border-white/[0.06] transition-colors last:border-b-0 ${
                activeRow ? "bg-cyan-300/[0.035]" : ""
              }`}
            >
              {row.map((cell) => {
                const change = getChordChangeAtBeat(changes, cell);
                const changeIndex = change
                  ? changes.findIndex((candidate) => candidate.id === change.id)
                  : -1;
                const sectionChanged = Boolean(
                  change?.section
                  && (changeIndex === 0 || change.section !== changes[changeIndex - 1]?.section)
                );
                const label = change ? getChordLabel(change) : "";
                const playingHere = cell.index === activeCellIndex;
                const sustainingActive = Boolean(change && change.id === activeChord?.id);
                const selectedIndex = typeof selectedCell === "number" ? selectedCell : selectedCell?.index;
                const selected = cell.index === selectedIndex
                  || Boolean(change && change.id === selectedChangeId);
                const playhead = playingHere
                  ? Math.max(0, Math.min(100, ((Number(currentTime) - cell.start) / cell.duration) * 100))
                  : 0;

                return (
                  <button
                    key={cell.index}
                    type="button"
                    onClick={() => {
                      onSelectCell?.(cell, change);
                      onSeek?.(cell.start);
                    }}
                    onDoubleClick={isEdit ? () => {
                      if (change) onUpdateChordChange?.(change, { focusSelector: true });
                      else onAddChordChange?.(cell);
                    } : undefined}
                    onKeyDown={isEdit && change ? (event) => {
                      if (event.key === "Delete") {
                        event.preventDefault();
                        event.stopPropagation();
                        onRemoveChordChange?.(change);
                      }
                    } : undefined}
                    style={{ width: `${cellWidth}px` }}
                    className={`relative flex h-24 shrink-0 flex-col justify-end overflow-hidden border-r border-white/[0.09] px-2 pb-2 pt-7 text-left transition sm:h-28 sm:px-3 ${
                      sectionChanged
                        ? "border-l-[3px] border-l-cyan-200/80"
                        : cell.isBarStart
                          ? "border-l-2 border-l-violet-300/40"
                          : ""
                    } ${
                      playingHere
                        ? "bg-cyan-300/12"
                        : selected
                          ? "bg-violet-400/12"
                          : "hover:bg-white/[0.045]"
                    }`}
                    aria-current={playingHere ? "true" : undefined}
                  >
                    <span className="absolute left-2 top-2 text-[9px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                      {cell.isBarStart ? `C${cell.barIndex + 1}` : cell.beatInBar}
                    </span>
                    {sectionChanged && (
                      <span className="absolute right-2 top-2 max-w-[55%] truncate text-[9px] font-semibold uppercase tracking-[0.13em] text-cyan-100">
                        {change.section}
                      </span>
                    )}
                    {label && (
                      <span className={`w-full truncate leading-none font-black tracking-tight ${
                        chordLabelSize(label.length)
                      } ${
                        sustainingActive
                          ? "text-cyan-100 drop-shadow-[0_0_10px_rgba(103,232,249,.65)]"
                          : "text-white"
                      }`}>
                        {label}
                      </span>
                    )}
                    <span className="mt-1 text-[10px] font-medium text-slate-500">{formatTime(cell.start)}</span>
                    {playingHere && (
                      <span
                        className="pointer-events-none absolute inset-y-0 z-20 w-[2px] bg-cyan-100 shadow-[0_0_15px_rgba(103,232,249,.92)]"
                        style={{ left: `${playhead}%` }}
                      />
                    )}
                  </button>
                );
              })}
            </div>
          );
        })}
        </div>
      </div>
    </div>
  );
});

ChordGridComposer.displayName = "ChordGridComposer";

export default ChordGridComposer;
